/**
 * 工作流列表面板组件
 *
 * 显示已保存的工作流列表，支持：
 * - 查看工作流列表
 * - 重命名工作流
 * - 删除工作流
 * - 选择工作流进行编辑
 * - 创建新工作流
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import type { WorkflowListItem } from '../../types/workflow';
import { workflowApi } from '../../services/workflowApi';

// ==================== 类型定义 ====================

interface WorkflowListPanelProps {
  /** 工作区 ID */
  workspaceId: number;
  /** 选择工作流进行编辑的回调 */
  onSelectWorkflow: (workflowId: string) => void;
  /** 创建新工作流的回调 */
  onCreateWorkflow: () => void;
  /** 自定义类名 */
  className?: string;
}

// ==================== 辅助函数 ====================

/** 格式化日期时间 */
function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 格式化相对时间 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return '刚刚';
  if (diffMins < 60) return `${diffMins} 分钟前`;
  if (diffHours < 24) return `${diffHours} 小时前`;
  if (diffDays < 7) return `${diffDays} 天前`;
  return formatDateTime(dateStr);
}

// ==================== 主组件 ====================

export const WorkflowListPanel: React.FC<WorkflowListPanelProps> = ({
  workspaceId,
  onSelectWorkflow,
  onCreateWorkflow,
  className,
}) => {
  // 状态
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // 加载工作流列表
  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await workflowApi.listWorkflows(workspaceId);
      setWorkflows(response.workflows || []);
    } catch (err) {
      console.error('[WorkflowListPanel] 加载工作流列表失败:', err);
      setError(err instanceof Error ? err.message : '加载工作流列表失败');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  // 初始加载
  useEffect(() => {
    if (workspaceId) {
      loadWorkflows();
    }
  }, [workspaceId, loadWorkflows]);

  // 开始重命名
  const handleStartRename = useCallback((workflow: WorkflowListItem) => {
    setEditingId(workflow.id);
    setEditingName(workflow.name);
  }, []);

  // 取消重命名
  const handleCancelRename = useCallback(() => {
    setEditingId(null);
    setEditingName('');
  }, []);

  // 确认重命名
  const handleConfirmRename = useCallback(async (workflowId: number) => {
    if (!editingName.trim()) {
      setError('工作流名称不能为空');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      await workflowApi.updateWorkflow(workflowId, {
        name: editingName.trim(),
      });

      setWorkflows((prev) =>
        prev.map((wf) =>
          wf.id === workflowId ? { ...wf, name: editingName.trim() } : wf
        )
      );

      setEditingId(null);
      setEditingName('');
    } catch (err) {
      console.error('[WorkflowListPanel] 重命名失败:', err);
      setError(err instanceof Error ? err.message : '重命名失败');
    } finally {
      setActionLoading(false);
    }
  }, [editingName]);

  // 处理重命名键盘事件
  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent, workflowId: number) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmRename(workflowId);
      } else if (e.key === 'Escape') {
        handleCancelRename();
      }
    },
    [handleConfirmRename, handleCancelRename]
  );

  // 开始删除
  const handleStartDelete = useCallback((workflowId: number) => {
    setDeletingId(workflowId);
  }, []);

  // 取消删除
  const handleCancelDelete = useCallback(() => {
    setDeletingId(null);
  }, []);

  // 确认删除
  const handleConfirmDelete = useCallback(async (workflowId: number) => {
    setActionLoading(true);
    setError(null);

    try {
      await workflowApi.deleteWorkflow(workflowId);

      setWorkflows((prev) => prev.filter((wf) => wf.id !== workflowId));
      setDeletingId(null);
    } catch (err) {
      console.error('[WorkflowListPanel] 删除失败:', err);
      setError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setActionLoading(false);
    }
  }, []);

  // 选择工作流进行编辑
  const handleSelectWorkflow = useCallback(
    (workflowId: number) => {
      onSelectWorkflow(String(workflowId));
    },
    [onSelectWorkflow]
  );

  // 渲染加载状态
  if (loading) {
    return (
      <div className={clsx('flex items-center justify-center h-64', className)}>
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-paper-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">加载中...</span>
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (error && workflows.length === 0) {
    return (
      <div className={clsx('flex flex-col items-center justify-center h-64 gap-4', className)}>
        <div className="text-red-500 text-sm">{error}</div>
        <button
          onClick={loadWorkflows}
          className="px-4 py-2 bg-ink-800 text-white rounded-lg hover:bg-ink-700 transition-colors text-sm"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className={clsx('flex flex-col h-full', className)}>
      {/* 头部：标题 + 创建按钮 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <h3 className="text-sm font-medium text-gray-900">工作流列表</h3>
        <button
          onClick={onCreateWorkflow}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink-800 text-white rounded-lg hover:bg-ink-700 transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          新建
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 列表区域 */}
      <div className="flex-1 overflow-y-auto">
        {workflows.length === 0 ? (
          // 空状态
          <div className="flex flex-col items-center justify-center h-full py-12 px-4">
            <div className="text-5xl mb-4">📋</div>
            <h4 className="text-base font-medium text-gray-900 mb-2">暂无工作流</h4>
            <p className="text-sm text-gray-500 text-center mb-4">
              点击上方「新建」按钮创建您的第一个工作流
            </p>
            <button
              onClick={onCreateWorkflow}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink-800 text-white rounded-lg hover:bg-ink-700 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              创建工作流
            </button>
          </div>
        ) : (
          // 工作流列表
          <ul className="divide-y divide-gray-100">
            {workflows.map((workflow) => (
              <li
                key={workflow.id}
                className="group hover:bg-gray-50 transition-colors"
              >
                {editingId === workflow.id ? (
                  // 重命名模式
                  <div className="px-4 py-3">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => handleRenameKeyDown(e, workflow.id)}
                      className="w-full px-3 py-1.5 border border-paper-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-paper-500 focus:ring-offset-1"
                      placeholder="输入工作流名称"
                      autoFocus
                      disabled={actionLoading}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleConfirmRename(workflow.id)}
                        disabled={actionLoading || !editingName.trim()}
                        className="px-3 py-1 bg-ink-800 text-white rounded-md text-xs font-medium hover:bg-ink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        确认
                      </button>
                      <button
                        onClick={handleCancelRename}
                        disabled={actionLoading}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition-colors"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                ) : deletingId === workflow.id ? (
                  // 删除确认模式
                  <div className="px-4 py-3">
                    <p className="text-sm text-gray-900 mb-2">
                      确定要删除「{workflow.name}」吗？此操作无法撤销。
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleConfirmDelete(workflow.id)}
                        disabled={actionLoading}
                        className="px-3 py-1 bg-red-500 text-white rounded-md text-xs font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        确认删除
                      </button>
                      <button
                        onClick={handleCancelDelete}
                        disabled={actionLoading}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition-colors"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                ) : (
                  // 正常显示模式
                  <div className="px-4 py-3 flex items-start justify-between">
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleSelectWorkflow(workflow.id)}
                    >
                      <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-paper-700 transition-colors">
                        {workflow.name}
                      </h4>
                      {workflow.description && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {workflow.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        更新于 {formatRelativeTime(workflow.updated_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartRename(workflow);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        title="重命名"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartDelete(workflow.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        title="删除"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 底部统计 */}
      {workflows.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <p className="text-xs text-gray-500">共 {workflows.length} 个工作流</p>
        </div>
      )}
    </div>
  );
};

export default WorkflowListPanel;
